import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import ImageUpload from "../ImageUpload";
import FileUploader from "../FileUploader";
import {
  CERT_TYPES, SKILLS, titleCase, certStatus, STATUS_STYLE, statusLabel,
} from "../../lib/crewHr";
import { Trash2, FileText, Plus } from "lucide-react";

const Section = ({ title, children }) => (
  <div className="rounded-xl border border-zinc-200 bg-white p-5">
    <h3 className="font-display text-base mb-4">{title}</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
  </div>
);

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="block text-xs font-medium text-zinc-600 mb-1">{label}</label>
    {children}
  </div>
);

const Chips = ({ options, value = [], onToggle, getKey = (o) => o, getLabel = (o) => titleCase(o), disabled }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => {
      const k = getKey(o);
      const on = value.includes(k);
      return (
        <button
          key={k} type="button" disabled={disabled}
          onClick={() => onToggle(k)}
          data-testid={`chip-${k}`}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
            on ? "bg-ink text-white border-ink" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400"
          } ${disabled ? "opacity-60" : ""}`}
        >{getLabel(o)}</button>
      );
    })}
  </div>
);

/**
 * Shared crew-profile editor used by both the crew member's master profile
 * (My Profile) and the yacht's editable copy (Crew HR → Profile tab).
 * Controlled: parent owns `value` + `set` (functional state setter) and Save.
 */
export default function ProfileForm({ value, set, yachtId = null, disabled = false, lockIdentity = false, identityHint = "Edit in Account preferences" }) {
  const v = value || {};
  const f = (key) => (e) => set((p) => ({ ...p, [key]: e?.target ? e.target.value : e }));
  const toggleArr = (key, item) => set((p) => {
    const arr = p[key] || [];
    return { ...p, [key]: arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item] };
  });

  const certs = v.certificates || [];
  const setCert = (i, patch) => set((p) => {
    const next = [...(p.certificates || [])];
    next[i] = { ...next[i], ...patch };
    return { ...p, certificates: next };
  });
  const addCert = () => set((p) => ({ ...p, certificates: [...(p.certificates || []), { type: "stcw" }] }));
  const rmCert = (i) => set((p) => ({ ...p, certificates: (p.certificates || []).filter((_, x) => x !== i) }));

  return (
    <div className="space-y-5" data-testid="profile-form">
      {/* Identity */}
      <Section title="Personal identity">
        <Field label="Profile photo">
          <div className="w-40">
            <ImageUpload value={v.profile_photo} onChange={(p) => set((x) => ({ ...x, profile_photo: p }))}
              folder="crew_profile" yachtId={yachtId} testid="profile-photo" aspect="aspect-square" label="photo" />
          </div>
        </Field>
        <Field label="CV">
          {v.cv_file ? (
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-zinc-500" />
              <a className="text-blue-600 underline" target="_blank" rel="noreferrer"
                href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${v.cv_file}`}>View CV</a>
              {!disabled && <button className="text-red-500" onClick={() => set((p) => ({ ...p, cv_file: null }))}><Trash2 className="h-4 w-4" /></button>}
            </div>
          ) : !disabled && (
            <FileUploader folder="crew_cv" yacht_id={yachtId} accept=".pdf,.doc,.docx"
              label="Upload CV" testid="pf-cv-upload" onUploaded={(path) => set((p) => ({ ...p, cv_file: path }))} />
          )}
        </Field>
        <Field label="Account Name">
          <Input value={v.full_name || ""} onChange={f("full_name")} disabled={disabled || lockIdentity} data-testid="pf-full-name" />
          {lockIdentity && <p className="text-[11px] text-zinc-500 mt-1">{identityHint}</p>}
        </Field>
        <Field label="Date of birth"><Input type="date" value={v.date_of_birth || ""} onChange={f("date_of_birth")} disabled={disabled} /></Field>
        <Field label="Nationality"><Input value={v.nationality || ""} onChange={f("nationality")} disabled={disabled} /></Field>
        <Field label="Gender"><Input value={v.gender || ""} onChange={f("gender")} disabled={disabled} /></Field>
        <Field label="Passport number"><Input value={v.passport_number || ""} onChange={f("passport_number")} disabled={disabled} /></Field>
        <Field label="Passport expiry"><Input type="date" value={v.passport_expiry_date || ""} onChange={f("passport_expiry_date")} disabled={disabled} /></Field>
        <Field label="National / Emirates ID"><Input value={v.national_id || ""} onChange={f("national_id")} disabled={disabled} /></Field>
        <Field label="Languages (comma separated)" full>
          <Input value={(v.languages || []).join(", ")} disabled={disabled}
            onChange={(e) => set((p) => ({ ...p, languages: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }))} />
        </Field>
        <Field label="Smoking" full>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!v.smoking} disabled={disabled}
            onChange={(e) => set((p) => ({ ...p, smoking: e.target.checked }))} /> Smoker</label>
        </Field>
      </Section>

      {/* Contact */}
      <Section title="Contact details">
        <Field label="Phone">
          <Input value={v.phone || ""} onChange={f("phone")} disabled={disabled || lockIdentity} data-testid="pf-phone" />
          {lockIdentity && <p className="text-[11px] text-zinc-500 mt-1">{identityHint}</p>}
        </Field>
        <Field label="WhatsApp"><Input value={v.whatsapp || ""} onChange={f("whatsapp")} disabled={disabled} /></Field>
        <Field label="Email">
          <Input value={v.email || ""} onChange={f("email")} disabled={disabled || lockIdentity} data-testid="pf-email" />
          {lockIdentity && <p className="text-[11px] text-zinc-500 mt-1">{identityHint}</p>}
        </Field>
        <Field label="Current location / home port"><Input value={v.current_location || ""} onChange={f("current_location")} disabled={disabled} /></Field>
        <Field label="Emergency contact name"><Input value={v.emergency_contact_name || ""} onChange={f("emergency_contact_name")} disabled={disabled} /></Field>
        <Field label="Emergency contact phone"><Input value={v.emergency_contact_phone || ""} onChange={f("emergency_contact_phone")} disabled={disabled} /></Field>
      </Section>

      {/* Certificates */}
      <div className="rounded-xl border border-zinc-200 bg-white p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-base">Certificates &amp; compliance</h3>
          {!disabled && <Button size="sm" variant="outline" onClick={addCert} data-testid="add-cert"><Plus className="h-4 w-4 mr-1" />Add certificate</Button>}
        </div>
        {certs.length === 0 && <p className="text-sm text-zinc-400">No certificates added.</p>}
        <div className="space-y-3">
          {certs.map((c, i) => {
            const st = certStatus(c.expiry_date);
            return (
              <div key={c.id || i} className="grid grid-cols-2 sm:grid-cols-[minmax(150px,1.5fr)_minmax(140px,1.2fr)_minmax(148px,1fr)_minmax(148px,1fr)_minmax(150px,auto)] gap-2 items-end border-b border-zinc-100 pb-3" data-testid={`cert-row-${i}`}>
                <div className="col-span-2 sm:col-span-1 min-w-0">
                  <label className="block text-[11px] text-zinc-500 mb-1">Type</label>
                  <select className="h-9 w-full rounded-md border border-input px-2 text-sm" value={c.type || "other"}
                    disabled={disabled} onChange={(e) => setCert(i, { type: e.target.value })}>
                    {CERT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
                <div className="col-span-2 sm:col-span-1 min-w-0">
                  <label className="block text-[11px] text-zinc-500 mb-1">Number</label>
                  <Input value={c.number || ""} disabled={disabled} onChange={(e) => setCert(i, { number: e.target.value })} />
                </div>
                <div className="min-w-0">
                  <label className="block text-[11px] text-zinc-500 mb-1">Issued</label>
                  <Input type="date" value={c.issue_date || ""} disabled={disabled} onChange={(e) => setCert(i, { issue_date: e.target.value })} />
                </div>
                <div className="min-w-0">
                  <label className="block text-[11px] text-zinc-500 mb-1">Expiry</label>
                  <Input type="date" value={c.expiry_date || ""} disabled={disabled} onChange={(e) => setCert(i, { expiry_date: e.target.value })} />
                </div>
                <div className="col-span-2 sm:col-span-1 flex items-center justify-start sm:justify-end gap-2">
                  {c.expiry_date && <Badge className={`${STATUS_STYLE[st.status]} border text-[10px] whitespace-nowrap`}>{statusLabel(st.status, st.days)}</Badge>}
                  {c.file_path ? (
                    <a className="text-blue-600" target="_blank" rel="noreferrer" href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${c.file_path}`}><FileText className="h-4 w-4" /></a>
                  ) : !disabled && (
                    <FileUploader folder="crew_cert" yacht_id={yachtId} accept=".pdf,.jpg,.jpeg,.png"
                      label="File" testid={`cert-upload-${i}`} onUploaded={(path) => setCert(i, { file_path: path })} />
                  )}
                  {!disabled && <button className="text-red-500 p-1" onClick={() => rmCert(i)} data-testid={`cert-del-${i}`}><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Skills */}
      <div className="rounded-xl border border-zinc-200 bg-white p-5">
        <h3 className="font-display text-base mb-4">Skills</h3>
        <Chips options={SKILLS} value={v.skills} onToggle={(k) => toggleArr("skills", k)} disabled={disabled} />
      </div>
    </div>
  );
}
